import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, ComposedChart, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Lock, Loader2, ArrowRight, Sparkles, MessageCircle, X, Send,
  TrendingUp, Target, Award, Users, Wallet, Briefcase, Crown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'tlj_pitch_token';

// =============== Palette ===============
const GOLD = '#D4AF37';
const GOLD_SOFT = '#E5C76B';
const INK = '#0A0A0A';
const SLATE = '#171717';
const BONE = '#F5F1E8';
const CHANNEL_COLORS = {
  etsy: '#D4AF37',
  ebay: '#9B7B3F',
  website: '#E5C76B',
  resellers: '#6B5430',
  referrals: '#3D2F1A',
  meta: '#7B5E1A',
  tiktok: '#A88A3D',
  google: '#C9A856',
};

// =============== Password Gate ===============
function PasswordGate({ onUnlock }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/pitch/verify`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onUnlock(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not verify');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-6 bg-[#0A0A0A] text-[#F5F1E8]" data-testid="pitch-gate">
      <div className="w-full max-w-md">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-3">Confidential · Investor Brief</div>
        <div className="text-[11px] tracking-widest uppercase text-[#F5F1E8]/40 mb-10">The Local Jewel</div>
        <h1 className="text-5xl md:text-6xl font-serif leading-[1.05] mb-6">Enter the room.</h1>
        <p className="text-[15px] text-[#F5F1E8]/60 mb-10 leading-relaxed">
          Access requires the shared password. This page contains confidential business, financial, and operational details intended for prospective investors only.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-2 block">Access password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              className="bg-transparent border-0 border-b border-[#F5F1E8]/20 rounded-none px-0 text-[#F5F1E8] focus-visible:ring-0 focus-visible:border-[#D4AF37] text-lg h-12"
              data-testid="pitch-password-input"
              autoFocus
            />
            {error && <div className="mt-3 text-sm text-red-400" data-testid="pitch-error">{error}</div>}
          </div>
          <Button
            type="submit"
            disabled={loading || !password}
            className="w-full h-12 bg-[#D4AF37] hover:bg-[#E5C76B] text-[#0A0A0A] font-medium tracking-wider uppercase text-[12px] rounded-none"
            data-testid="pitch-unlock-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Open the brief <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </form>
        <p className="mt-12 text-[11px] text-[#F5F1E8]/30 leading-relaxed">
          By continuing you agree to keep the contents of this document confidential.
        </p>
        <p className="mt-16 text-[10px] tracking-widest uppercase text-[#F5F1E8]/20">© 2026 The Local Jewel · Confidential</p>
      </div>
    </div>
  );
}

// =============== Section helpers ===============
const Section = ({ id, eyebrow, title, subtitle, children }) => (
  <section id={id} className="py-20 md:py-28 border-t border-[#F5F1E8]/5" data-testid={`section-${id}`}>
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      {eyebrow && <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-3">{eyebrow}</div>}
      <h2 className="text-3xl md:text-5xl font-serif leading-[1.1] mb-4 text-[#F5F1E8]">{title}</h2>
      {subtitle && <p className="text-[15px] text-[#F5F1E8]/60 max-w-3xl mb-12 leading-relaxed">{subtitle}</p>}
      {children}
    </div>
  </section>
);

const StatCard = ({ label, value, accent }) => (
  <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6 hover:border-[#D4AF37]/40 transition-colors group">
    <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-2">{label}</div>
    <div className={`text-3xl md:text-4xl font-serif ${accent ? 'text-[#D4AF37]' : 'text-[#F5F1E8]'} group-hover:text-[#D4AF37] transition-colors`}>
      {value}
    </div>
  </div>
);

const formatUSD = (n) => {
  if (typeof n !== 'number') return n;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.00$/, '')}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `$${n.toLocaleString()}`;
};

const chartAxisStyle = { fill: '#F5F1E8', opacity: 0.45, fontSize: 11, fontFamily: 'inherit' };

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#0A0A0A] border border-[#D4AF37]/40 px-4 py-3 shadow-2xl">
      <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="text-sm text-[#F5F1E8] flex items-center gap-2 leading-tight">
          <span className="w-2 h-2" style={{ background: p.color }} />
          <span className="text-[#F5F1E8]/60">{p.name}:</span>
          <span className="font-medium">
            {typeof p.value === 'number' && p.name.toLowerCase().includes('rev')
              ? formatUSD(p.value)
              : typeof p.value === 'number' && p.value > 1000
                ? formatUSD(p.value)
                : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// =============== AI Chat Widget ===============
function ChatWidget({ token }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const stored = sessionStorage.getItem('tlj_pitch_chat');
    if (stored) try { return JSON.parse(stored); } catch (e) { /* ignore */ }
    return [{
      role: 'assistant',
      content: "I'm The Local Jewel's investor Q&A assistant. Ask me anything about the raise, traction, projections, or how we plan to scale. I have the full brief in context.",
    }];
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `pitch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem('tlj_pitch_chat', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || sending) return;
    const next = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/pitch/chat`,
        {
          question: q,
          history: next.slice(0, -1).slice(-16),
          session_id: sessionId,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 },
      );
      setMessages([...next, { role: 'assistant', content: res.data.answer }]);
    } catch (err) {
      setMessages([
        ...next,
        { role: 'assistant', content: `Sorry — I ran into an error: ${err.response?.data?.detail || err.message}. Please try again.` },
      ]);
    } finally {
      setSending(false);
    }
  };

  const suggested = [
    "What's the ask and ownership?",
    "What's the Etsy ROAS proof?",
    "Show the 4-month projection",
    "How will the $100K be spent?",
    "What's the 3-year revenue trajectory?",
  ];

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 group bg-[#D4AF37] hover:bg-[#E5C76B] text-[#0A0A0A] rounded-full px-5 py-3.5 shadow-2xl flex items-center gap-2 transition-all hover:scale-105"
          data-testid="pitch-chat-open-btn"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-[12px] font-medium tracking-wider uppercase">Ask the brief</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[min(92vw,420px)] h-[min(80vh,640px)] bg-[#0A0A0A] border border-[#D4AF37]/30 shadow-2xl flex flex-col" data-testid="pitch-chat-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#F5F1E8]/10">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80">Investor Q&A</div>
              <div className="text-[13px] font-medium text-[#F5F1E8] mt-0.5">The Local Jewel</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#F5F1E8]/50 hover:text-[#F5F1E8]" data-testid="pitch-chat-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`text-sm leading-relaxed ${m.role === 'user' ? 'text-right' : ''}`}>
                <div className={`inline-block px-3.5 py-2.5 max-w-[88%] ${
                  m.role === 'user'
                    ? 'bg-[#D4AF37] text-[#0A0A0A]'
                    : 'bg-[#171717] text-[#F5F1E8] border border-[#F5F1E8]/5'
                }`}>
                  <div className="whitespace-pre-wrap text-[13px]" style={{ wordBreak: 'break-word' }}>
                    {m.content}
                  </div>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-[12px] text-[#F5F1E8]/40">
                <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
              </div>
            )}
            {messages.length <= 1 && (
              <div className="pt-2 flex flex-wrap gap-1.5">
                {suggested.map((s) => (
                  <button
                    key={s}
                    onClick={() => { setInput(s); }}
                    className="text-[11px] px-2.5 py-1 border border-[#F5F1E8]/15 text-[#F5F1E8]/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <form onSubmit={send} className="border-t border-[#F5F1E8]/10 p-3 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about traction, raise, projections…"
              disabled={sending}
              className="flex-1 bg-transparent text-[13px] text-[#F5F1E8] placeholder:text-[#F5F1E8]/30 focus:outline-none px-2"
              data-testid="pitch-chat-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="bg-[#D4AF37] hover:bg-[#E5C76B] disabled:opacity-30 text-[#0A0A0A] p-2 transition-colors"
              data-testid="pitch-chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

// =============== Main Pitch Page ===============
export default function Pitch() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/pitch/data`);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadData();
  }, [token, loadData]);

  if (!token) return <PasswordGate onUnlock={(t) => setToken(t)} />;

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const ramp = data.four_month_ramp;
  const adSpend = data.four_month_ad_spend;
  const rev = data.four_month_revenue;
  const three = data.three_year_projection.map((y) => ({
    ...y,
    rev_avg: (y.total_rev_low + y.total_rev_high) / 2,
    gm_avg: (y.gm_low + y.gm_high) / 2,
    contribution_avg: (y.contribution_low + y.contribution_high) / 2,
  }));
  const totalUseOfFunds = data.use_of_funds.reduce((s, x) => s + x.amount, 0);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F1E8] font-sans antialiased" data-testid="pitch-page">
      <style>{`
        .pitch-serif { font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif; }
      `}</style>

      {/* ===== HERO ===== */}
      <header className="relative overflow-hidden border-b border-[#F5F1E8]/5">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, #D4AF37 0%, transparent 50%), radial-gradient(circle at 80% 70%, #D4AF37 0%, transparent 40%)',
        }} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-20 md:pb-28">
          <div className="flex items-center gap-3 mb-8">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80">Confidential · Investor Brief</span>
            <span className="w-12 h-px bg-[#D4AF37]/40" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#F5F1E8]/40">2026</span>
          </div>
          <div className="text-[11px] tracking-widest uppercase text-[#F5F1E8]/40 mb-8">The Local Jewel</div>
          <h1 className="text-5xl md:text-7xl pitch-serif leading-[1.02] mb-6 max-w-4xl">
            From 6 rings a month to a <span className="text-[#D4AF37]">$4M+ jewelry brand</span> in three years.
          </h1>
          <p className="text-lg md:text-xl text-[#F5F1E8]/60 max-w-3xl mb-12 leading-relaxed">
            We're raising <strong className="text-[#F5F1E8]">$100K in equity at a $1.5M pre-money valuation</strong> to scale our lab-grown diamond engagement ring business from a 6-ring run-rate to <strong className="text-[#F5F1E8]">100 rings per month</strong>, then expand into a full lab-grown jewelry brand.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Raise" value="$100K" accent />
            <StatCard label="Pre-money" value="$1.5M" />
            <StatCard label="Ownership" value="6.25%" />
            <StatCard label="Target run-rate" value="100/mo" />
          </div>
        </div>
      </header>

      {/* ===== TRACTION ===== */}
      <Section
        id="traction"
        eyebrow="01 · Where we are today"
        title="Traction is real. Margins are healthy. Demand is proven."
        subtitle="The numbers behind a business that's already running — averaging 6 rings/month with a best month of 30, $200K+ in bank-verified deposits in the trailing twelve months, and a 37.75x Etsy Ads ROAS to prove paid demand."
      >
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-12">
          {data.traction.map((t, i) => (
            <div key={i} className="bg-[#171717] border border-[#F5F1E8]/5 p-5 hover:border-[#D4AF37]/40 transition-colors">
              <div className="text-2xl md:text-3xl pitch-serif text-[#D4AF37] mb-1">{t.value}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/50 leading-relaxed">{t.label}</div>
            </div>
          ))}
        </div>

        {/* Etsy proof highlight */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-[#171717] to-[#0A0A0A] border border-[#D4AF37]/30 p-8 relative overflow-hidden">
            <div className="absolute top-4 right-4 text-[10px] uppercase tracking-widest text-[#D4AF37]/60 flex items-center gap-1.5">
              <Crown className="w-3 h-3" /> Strongest proof
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]/80 mb-2">Etsy Ads · screenshot-verified</div>
            <div className="text-6xl pitch-serif text-[#D4AF37] mb-3">37.75x</div>
            <div className="text-sm text-[#F5F1E8]/60 mb-6">Return on ad spend</div>
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-[#F5F1E8]/10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Spend</div>
                <div className="text-lg pitch-serif text-[#F5F1E8] mt-1">$417</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Revenue</div>
                <div className="text-lg pitch-serif text-[#F5F1E8] mt-1">$15.7K</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Orders</div>
                <div className="text-lg pitch-serif text-[#F5F1E8] mt-1">9</div>
              </div>
            </div>
            <div className="mt-6 text-[12px] text-[#F5F1E8]/50 leading-relaxed">
              Etsy is currently the strongest proof point — credible reason to allocate meaningful spend there first while testing other channels.
            </div>
          </div>
          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-8">
            <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-2">Meta lead-form test</div>
            <div className="text-3xl pitch-serif text-[#F5F1E8] mb-2">4 quality leads from <span className="text-[#D4AF37]">$25</span></div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="bg-[#0A0A0A] p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Impressions</div>
                <div className="text-sm text-[#F5F1E8] mt-1">~3,000</div>
              </div>
              <div className="bg-[#0A0A0A] p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Quote-ready</div>
                <div className="text-sm text-[#F5F1E8] mt-1">2 of 4</div>
              </div>
            </div>
            <div className="mt-6 text-[12px] text-[#F5F1E8]/50 leading-relaxed">
              Conversion not yet proven, but lead quality is strong. Next step: better follow-up, retargeting, payment-plan messaging.
            </div>
          </div>
        </div>
      </Section>

      {/* ===== SNAPSHOT ===== */}
      <Section
        id="snapshot"
        eyebrow="02 · Unit economics"
        title="$700 gross margin, paid upfront, shipped in days."
        subtitle="The mechanics that make every additional ring profitable from day one."
      >
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6">
            <Wallet className="w-5 h-5 text-[#D4AF37] mb-3" />
            <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-2">Avg selling price</div>
            <div className="text-2xl pitch-serif text-[#F5F1E8]">$1,700 <span className="text-sm text-[#F5F1E8]/40">→ $1,600 at scale</span></div>
          </div>
          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6">
            <Target className="w-5 h-5 text-[#D4AF37] mb-3" />
            <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-2">Gross margin / ring</div>
            <div className="text-2xl pitch-serif text-[#D4AF37]">$700 <span className="text-sm text-[#F5F1E8]/40">/ ~43.75%</span></div>
          </div>
          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6">
            <TrendingUp className="w-5 h-5 text-[#D4AF37] mb-3" />
            <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-2">Production timeline</div>
            <div className="text-2xl pitch-serif text-[#F5F1E8]">2–5 days</div>
          </div>
          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6">
            <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-2">Customer terms</div>
            <div className="text-lg text-[#F5F1E8]">Full payment upfront</div>
            <div className="text-[12px] text-[#F5F1E8]/50 mt-2">Negative working capital structure</div>
          </div>
          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6">
            <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-2">Production cost</div>
            <div className="text-lg text-[#F5F1E8]">~$800 / ring</div>
            <div className="text-[12px] text-[#F5F1E8]/50 mt-2">Diamond, setting, CAD, labor</div>
          </div>
          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6">
            <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-2">Monthly fixed ops</div>
            <div className="text-lg text-[#F5F1E8]">~$3,000/mo</div>
            <div className="text-[12px] text-[#F5F1E8]/50 mt-2">Lean — AI/automation stack absorbs scale</div>
          </div>
        </div>
      </Section>

      {/* ===== 4-MONTH RAMP ===== */}
      <Section
        id="ramp"
        eyebrow="03 · The 4-month plan"
        title="$100K in. $424K revenue out. 265 rings shipped."
        subtitle="The first four months take us from 6 → 100 rings per month, producing $185.5K in gross margin and $120.5K in contribution after ads and ops — with $25K still deployed in rolling reseller inventory."
      >
        <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6 md:p-8 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Rings sold per month — by channel</div>
          <div className="text-sm text-[#F5F1E8]/50 mb-6">Etsy and website paid (Meta/TikTok/Google) lead the ramp; resellers compound steadily.</div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={ramp} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 6" stroke="#F5F1E8" opacity={0.05} />
              <XAxis dataKey="month" tick={chartAxisStyle} axisLine={false} tickLine={false} />
              <YAxis tick={chartAxisStyle} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: '#D4AF37', fillOpacity: 0.05 }} />
              <Legend wrapperStyle={{ paddingTop: 16, fontSize: 11, color: '#F5F1E8' }} iconType="square" />
              <Bar dataKey="etsy" stackId="r" fill={CHANNEL_COLORS.etsy} name="Etsy" />
              <Bar dataKey="website" stackId="r" fill={CHANNEL_COLORS.website} name="Website (Meta/TT/G)" />
              <Bar dataKey="resellers" stackId="r" fill={CHANNEL_COLORS.resellers} name="Resellers" />
              <Bar dataKey="ebay" stackId="r" fill={CHANNEL_COLORS.ebay} name="eBay" />
              <Bar dataKey="referrals" stackId="r" fill={CHANNEL_COLORS.referrals} name="Referrals" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6 md:p-8">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Ad spend by channel</div>
            <div className="text-sm text-[#F5F1E8]/50 mb-6">$45K total — front-loaded on Etsy, scaling Meta as conversion improves.</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={adSpend} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 6" stroke="#F5F1E8" opacity={0.05} />
                <XAxis dataKey="month" tick={chartAxisStyle} axisLine={false} tickLine={false} />
                <YAxis tick={chartAxisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v/1000}K`} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: '#D4AF37', fillOpacity: 0.05 }} />
                <Legend wrapperStyle={{ paddingTop: 12, fontSize: 11, color: '#F5F1E8' }} iconType="square" />
                <Bar dataKey="etsy" stackId="a" fill={CHANNEL_COLORS.etsy} name="Etsy" />
                <Bar dataKey="meta" stackId="a" fill={CHANNEL_COLORS.meta} name="Meta" />
                <Bar dataKey="tiktok" stackId="a" fill={CHANNEL_COLORS.tiktok} name="TikTok" />
                <Bar dataKey="google" stackId="a" fill={CHANNEL_COLORS.google} name="Google" />
                <Bar dataKey="ebay" stackId="a" fill={CHANNEL_COLORS.ebay} name="eBay" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6 md:p-8">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Revenue, gross margin & contribution</div>
            <div className="text-sm text-[#F5F1E8]/50 mb-6">Each month builds compounding contribution after ads + ops.</div>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={rev} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 6" stroke="#F5F1E8" opacity={0.05} />
                <XAxis dataKey="month" tick={chartAxisStyle} axisLine={false} tickLine={false} />
                <YAxis tick={chartAxisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v/1000}K`} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: '#D4AF37', fillOpacity: 0.05 }} />
                <Legend wrapperStyle={{ paddingTop: 12, fontSize: 11, color: '#F5F1E8' }} iconType="square" />
                <Bar dataKey="revenue" fill={GOLD} name="Revenue" />
                <Bar dataKey="gross_margin" fill={GOLD_SOFT} name="Gross margin" />
                <Line type="monotone" dataKey="contribution" stroke="#F5F1E8" strokeWidth={2} name="Contribution" dot={{ r: 4, fill: '#F5F1E8' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 4-month totals row */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Rings shipped" value={data.four_month_totals.rings} />
          <StatCard label="Revenue" value={formatUSD(data.four_month_totals.revenue)} />
          <StatCard label="Gross margin" value={formatUSD(data.four_month_totals.gross_margin)} accent />
          <StatCard label="Contribution" value={formatUSD(data.four_month_totals.contribution)} accent />
        </div>
      </Section>

      {/* ===== MONTH 4 RUN-RATE ===== */}
      <Section
        id="runrate"
        eyebrow="04 · By month 4"
        title="A $1.92M run-rate business with $480K–$594K in annual contribution."
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatCard label="Monthly rings" value={data.month4_runrate.monthly_rings} />
          <StatCard label="Monthly revenue" value={formatUSD(data.month4_runrate.monthly_revenue)} accent />
          <StatCard label="Monthly gross margin" value={formatUSD(data.month4_runrate.monthly_gross_margin)} />
          <StatCard label="Annual revenue" value={data.month4_runrate.annual_revenue} accent />
          <StatCard label="Annual gross margin" value={data.month4_runrate.annual_gross_margin} />
          <StatCard label="Annual contribution" value={data.month4_runrate.annual_contribution_range} accent />
        </div>
      </Section>

      {/* ===== 3-YEAR PROJECTION ===== */}
      <Section
        id="three-year"
        eyebrow="05 · Three years out"
        title="From engagement rings to a full jewelry brand."
        subtitle="Engagement rings are the customer-acquisition wedge. Wedding bands, anniversary jewelry, studs, and tennis bracelets unlock real lifetime value — and the reseller network becomes a recurring distribution channel."
      >
        <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6 md:p-8 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Revenue, gross margin & operating contribution by year</div>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={three} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 6" stroke="#F5F1E8" opacity={0.05} />
              <XAxis dataKey="year" tick={chartAxisStyle} axisLine={false} tickLine={false} />
              <YAxis tick={chartAxisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v/1_000_000).toFixed(1)}M`} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: '#D4AF37', fillOpacity: 0.05 }} />
              <Legend wrapperStyle={{ paddingTop: 16, fontSize: 11, color: '#F5F1E8' }} iconType="square" />
              <Bar dataKey="rev_avg" fill={GOLD} name="Revenue" />
              <Bar dataKey="gm_avg" fill={GOLD_SOFT} name="Gross margin" />
              <Line type="monotone" dataKey="contribution_avg" stroke="#F5F1E8" strokeWidth={2.5} name="Operating contribution" dot={{ r: 5, fill: '#F5F1E8' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Table */}
        <div className="bg-[#171717] border border-[#F5F1E8]/5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#F5F1E8]/10">
                <th className="text-left p-4 text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Year</th>
                <th className="text-right p-4 text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Rings</th>
                <th className="text-right p-4 text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Ring revenue</th>
                <th className="text-right p-4 text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Expansion revenue</th>
                <th className="text-right p-4 text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Total revenue</th>
                <th className="text-right p-4 text-[10px] uppercase tracking-widest text-[#D4AF37]">Gross margin</th>
                <th className="text-right p-4 text-[10px] uppercase tracking-widest text-[#D4AF37]">Op. contribution</th>
              </tr>
            </thead>
            <tbody>
              {data.three_year_projection.map((y, i) => (
                <tr key={i} className="border-b border-[#F5F1E8]/5">
                  <td className="p-4 text-[#F5F1E8]">{y.year}</td>
                  <td className="p-4 text-right text-[#F5F1E8]/80">{y.rings.toLocaleString()}</td>
                  <td className="p-4 text-right text-[#F5F1E8]/80">{formatUSD(y.ring_rev)}</td>
                  <td className="p-4 text-right text-[#F5F1E8]/80">
                    {y.expansion_rev_low === y.expansion_rev_high ? formatUSD(y.expansion_rev_low) : `${formatUSD(y.expansion_rev_low)}–${formatUSD(y.expansion_rev_high)}`}
                  </td>
                  <td className="p-4 text-right text-[#F5F1E8] font-medium">
                    {y.total_rev_low === y.total_rev_high ? formatUSD(y.total_rev_low) : `${formatUSD(y.total_rev_low)}–${formatUSD(y.total_rev_high)}`}
                  </td>
                  <td className="p-4 text-right text-[#D4AF37] font-medium">
                    {y.gm_low === y.gm_high ? formatUSD(y.gm_low) : `${formatUSD(y.gm_low)}–${formatUSD(y.gm_high)}`}
                  </td>
                  <td className="p-4 text-right text-[#D4AF37] font-medium">
                    {y.contribution_low === y.contribution_high ? formatUSD(y.contribution_low) : `${formatUSD(y.contribution_low)}–${formatUSD(y.contribution_high)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Growth drivers */}
        <div className="mt-12 grid md:grid-cols-3 gap-3">
          {data.growth_drivers.map((g, i) => (
            <div key={i} className="bg-[#171717] border border-[#F5F1E8]/5 p-5 hover:border-[#D4AF37]/30 transition-colors">
              <div className="text-[13px] font-medium text-[#F5F1E8] mb-1.5">{g.driver}</div>
              <div className="text-[12px] text-[#F5F1E8]/50 leading-relaxed">{g.why}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== LTV ===== */}
      <Section
        id="ltv"
        eyebrow="06 · Lifetime value"
        title="The first engagement ring is the start of a relationship."
        subtitle="Engagement → wedding bands → anniversary jewelry → referrals. Each ring opens a long-tail of purchase windows. The reseller becomes its own recurring distribution channel."
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-4">Customer LTV path</div>
            <div className="space-y-2">
              {data.ltv_customer.map((c, i) => (
                <div key={i} className="bg-[#171717] border border-[#F5F1E8]/5 p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-[13px] text-[#F5F1E8] font-medium">{c.type}</div>
                    <div className="text-[11px] text-[#F5F1E8]/50 mt-0.5">{c.timing}</div>
                  </div>
                  <div className="text-[#D4AF37] text-[14px] pitch-serif font-medium">{c.value}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-4">Reseller LTV model</div>
            <div className="bg-[#171717] border border-[#F5F1E8]/5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#F5F1E8]/10">
                    <th className="text-left p-3 text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Metric</th>
                    <th className="text-right p-3 text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">Conservative</th>
                    <th className="text-right p-3 text-[10px] uppercase tracking-widest text-[#D4AF37]">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ltv_reseller.map((r, i) => (
                    <tr key={i} className="border-b border-[#F5F1E8]/5">
                      <td className="p-3 text-[#F5F1E8]/80">{r.metric}</td>
                      <td className="p-3 text-right text-[#F5F1E8]/70">{r.conservative}</td>
                      <td className="p-3 text-right text-[#D4AF37] font-medium">{r.target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== USE OF FUNDS ===== */}
      <Section
        id="funds"
        eyebrow="07 · Use of funds"
        title="$100K, deployed deliberately."
        subtitle="$25K of this stays as working capital in reseller inventory — not burn — and rotates back into liquid stock as resellers sell."
      >
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.use_of_funds}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={2}
                  dataKey="amount"
                  stroke="none"
                >
                  {data.use_of_funds.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-center text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mt-2">Total · {formatUSD(totalUseOfFunds)}</div>
          </div>
          <div className="space-y-3">
            {data.use_of_funds.map((u, i) => {
              const pct = ((u.amount / totalUseOfFunds) * 100).toFixed(1);
              return (
                <div key={i} className="bg-[#171717] border border-[#F5F1E8]/5 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3" style={{ background: u.color }} />
                      <div className="text-[13px] text-[#F5F1E8] font-medium">{u.category}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[15px] text-[#D4AF37] pitch-serif">{formatUSD(u.amount)}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">{pct}%</div>
                    </div>
                  </div>
                  <div className="h-1 bg-[#F5F1E8]/5 overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, background: u.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ===== INVESTOR RETURNS ===== */}
      <Section
        id="returns"
        eyebrow="08 · The equity math"
        title="At a $1.5M pre-money, $100K returns 6.25%."
        subtitle="Equity, not debt — return comes from increase in company value, not scheduled repayment."
      >
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {data.investor_returns.map((r, i) => (
            <div key={i} className={`p-6 border ${r.recommended ? 'bg-gradient-to-br from-[#171717] to-[#0A0A0A] border-[#D4AF37]/40' : 'bg-[#171717] border-[#F5F1E8]/5'} ${data.investor_returns.length === 3 && i === 0 ? 'md:col-span-1' : ''} ${r.recommended ? 'md:col-span-2' : ''}`}>
              {r.recommended && (
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]/80 mb-3 flex items-center gap-1.5">
                  <Crown className="w-3 h-3" /> Recommended structure
                </div>
              )}
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-1">Pre-money</div>
                  <div className="text-xl pitch-serif text-[#F5F1E8]">{r.pre_money}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-1">Post-money</div>
                  <div className="text-xl pitch-serif text-[#F5F1E8]">{r.post_money}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40 mb-1">Ownership</div>
                  <div className="text-xl pitch-serif text-[#D4AF37]">{r.ownership_pct}%</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#171717] border border-[#F5F1E8]/5 p-6 md:p-8">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Stake value at future company values</div>
          <div className="text-sm text-[#F5F1E8]/50 mb-6">At 6.25% ownership.</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.stake_value_scenarios} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 6" stroke="#F5F1E8" opacity={0.05} />
              <XAxis dataKey="future_value" tick={chartAxisStyle} axisLine={false} tickLine={false} />
              <YAxis tick={chartAxisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v/1000}K`} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: '#D4AF37', fillOpacity: 0.05 }} />
              <Bar dataKey="stake" fill={GOLD} name="Stake value" />
            </BarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-4 gap-3 mt-6 pt-6 border-t border-[#F5F1E8]/10">
            {data.stake_value_scenarios.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-[10px] uppercase tracking-widest text-[#F5F1E8]/40">{s.future_value} company</div>
                <div className="text-lg pitch-serif text-[#D4AF37] mt-1">{s.multiple}x</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== STRENGTHS ===== */}
      <Section
        id="strengths"
        eyebrow="09 · Why now"
        title="Ten compounding advantages."
      >
        <div className="grid md:grid-cols-2 gap-3">
          {data.strengths.map((s, i) => (
            <div key={i} className="bg-[#171717] border border-[#F5F1E8]/5 p-5 hover:border-[#D4AF37]/30 transition-colors flex gap-4">
              <div className="text-[#D4AF37]/40 pitch-serif text-2xl w-8 flex-shrink-0">{String(i + 1).padStart(2, '0')}</div>
              <div className="flex-1">
                <div className="text-[14px] text-[#F5F1E8] font-medium mb-1">{s.strength}</div>
                <div className="text-[12px] text-[#F5F1E8]/50 leading-relaxed">{s.why}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== TEAM ===== */}
      <Section
        id="team"
        eyebrow="10 · Team plan"
        title="Hires directly tied to listings, leads & creative."
        subtitle="No general overhead — every role plugs into a specific bottleneck the model needs solved to hit 100 rings/month."
      >
        <div className="grid md:grid-cols-2 gap-3">
          {data.team_expansion.map((r, i) => (
            <div key={i} className="bg-[#171717] border border-[#F5F1E8]/5 p-5 flex items-start gap-4">
              <Users className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[14px] text-[#F5F1E8] font-medium mb-1">{r.role}</div>
                <div className="text-[12px] text-[#F5F1E8]/50 leading-relaxed">{r.purpose}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== CLOSING SUMMARY ===== */}
      <Section
        id="summary"
        eyebrow="The bottom line"
        title="The case for investing in The Local Jewel."
      >
        <div className="bg-gradient-to-br from-[#171717] to-[#0A0A0A] border border-[#D4AF37]/30 p-8 md:p-12">
          <div className="space-y-4 text-[15px] md:text-[16px] text-[#F5F1E8]/80 leading-relaxed">
            <p>
              We sell <strong className="text-[#F5F1E8]">custom lab-grown diamond engagement rings</strong>. Customers pay <strong className="text-[#F5F1E8]">100% upfront</strong>. We ship in <strong className="text-[#F5F1E8]">2–5 days</strong>. Every ring nets <strong className="text-[#D4AF37]">~$700 in gross margin</strong>.
            </p>
            <p>
              Today we average 6 rings/month. We've already hit 30 in our best month. Bank-verified deposits over the last twelve months: <strong className="text-[#F5F1E8]">$200K+</strong>. Etsy ads are running at <strong className="text-[#D4AF37]">37.75x ROAS</strong>. Four resellers are selling without inventory on hand. eBay is making sales with zero ad spend. TikTok is sending us organic inquiries.
            </p>
            <p>
              With <strong className="text-[#D4AF37]">$100K in equity</strong> at a <strong className="text-[#F5F1E8]">$1.5M pre-money valuation</strong>, we can hit a 100 rings/month run-rate in four months — projected to ship 265 rings, $424K in revenue, $185.5K in gross margin, and $120.5K in contribution after ads and ops. That run-rate annualizes to <strong className="text-[#F5F1E8]">$1.92M in revenue and $840K in gross margin</strong>.
            </p>
            <p>
              Beyond month 4, the same customer who bought the engagement ring becomes a buyer of wedding bands, anniversary jewelry, studs, tennis bracelets, and a source of referrals. Each reseller becomes a recurring channel. By year three: <strong className="text-[#D4AF37]">$4M+ revenue, $1.76M gross margin, ~$960K in operating contribution</strong>.
            </p>
            <p className="pt-4 border-t border-[#F5F1E8]/10 text-[#F5F1E8]/60 italic">
              Have a question that isn't answered above? Tap <strong className="text-[#D4AF37]">Ask the brief</strong> in the corner — the assistant has the full investor brief loaded and will answer in seconds.
            </p>
          </div>
        </div>
      </Section>

      <footer className="py-10 border-t border-[#F5F1E8]/5 text-center text-[10px] tracking-widest uppercase text-[#F5F1E8]/30">
        © 2026 The Local Jewel · Confidential — for prospective investors only
      </footer>

      <ChatWidget token={token} />
    </div>
  );
}
